/**
 * DeathCam — the screen seven of eight paying players end up looking at.
 *
 * GAME-DESIGN §5: "Their screen is not allowed to be nothing." A player who just
 * lost a buy-in and gets a black screen has been given a reason to close the
 * tab, and requeue is where the session either continues or ends.
 *
 * The sequence:
 *   1. KILLER VIEW for 4s — the camera follows whoever killed you, so the death
 *      is legible rather than mysterious. Falls straight through to free camera
 *      when the zone killed you, because there is nobody to watch.
 *   2. FREE CAMERA — fly the arena and watch the rest of the match.
 *   3. LIVE POT TICKER — what the survivors are still fighting over, updated
 *      from the room's Schema for as long as the match runs.
 *   4. ONE-TAP REQUEUE — a single control, thumb-sized, always in the same spot.
 *
 * Spectator control of other players is deliberately absent: this is the dead
 * player's screen, not a spectator mode.
 */

import { LOBBY_URL } from '../config.js';
import { cameraState } from '../state.js';
import { formatEth } from './CombatHUD.js';
import { escapeHtml } from './html.js';

const KILLER_VIEW_MS = 4000;

export class DeathCam {
  constructor(cameraController, { onRequeue } = {}) {
    this.controller = cameraController;
    this.root = document.getElementById('death-cam');
    this.active = false;
    this._phase = 'idle';
    this._since = 0;
    this._onRequeue = onRequeue ?? (() => window.location.assign(LOBBY_URL));
    this._build();
  }

  _build() {
    // The loss is clean rather than punishing: the word is red because it is
    // your money leaving, the pot stays gold and carries the only pulsing dot
    // on the screen (the match is still happening), and REQUEUE is the
    // biggest, brightest and only button.
    this.root.innerHTML = `
      <div class="dc-banner">
        <div class="shout headline" data-headline>ELIMINATED</div>
        <div class="sub" data-sub></div>
      </div>
      <div class="dc-hint" data-hint>WASD / drag to fly · watching the match</div>
      <div class="dc-panel">
        <div class="dc-stats">
          <div class="dc-stat"><span class="label">Placement</span><span class="value" data-place>—</span></div>
          <div class="dc-stat"><span class="label">Kills</span><span class="value" data-kills>0</span></div>
          <div class="dc-stat"><span class="label">Damage</span><span class="value" data-damage>0</span></div>
          <div class="dc-stat pot"><span class="label">Live pot</span><span class="value"><span class="dot"></span><span data-pot>—</span></span></div>
        </div>
        <div class="dc-cta"><button class="dc-btn" data-requeue>REQUEUE</button></div>
      </div>
    `;
    this.headlineEl = this.root.querySelector('[data-headline]');
    this.subEl = this.root.querySelector('[data-sub]');
    this.hintEl = this.root.querySelector('[data-hint]');
    this.placeEl = this.root.querySelector('[data-place]');
    this.killsEl = this.root.querySelector('[data-kills]');
    this.damageEl = this.root.querySelector('[data-damage]');
    this.potEl = this.root.querySelector('[data-pot]');

    const btn = this.root.querySelector('[data-requeue]');
    // Both events: `click` alone has a ~300ms delay on some mobile browsers, and
    // this is the one control that has to feel instant.
    btn.addEventListener('click', () => this._onRequeue());
    btn.addEventListener(
      'touchend',
      (e) => {
        e.preventDefault();
        this._onRequeue();
      },
      { passive: false }
    );

    // Mounted LAST, and that ordering is load-bearing: the requeue button lives
    // inside `.dc-panel`, so moving the panel out of `this.root` before the
    // query above would hand it null and throw in the constructor.
    //
    // Same deal as the ability row — ours to drive, the dock's to place. It
    // does cost the gate we used to get for free: `#death-cam` is display:none
    // until it goes active, which hid everything inside it, and a child of the
    // dock is not inside it any more. `hidden` is that gate restated, toggled
    // in the three places that own this screen: show, showResult and hide.
    this.panelEl = this.root.querySelector('.dc-panel');
    const dock = document.getElementById('bottom-dock');
    if (dock) {
      dock.appendChild(this.hintEl);
      dock.appendChild(this.panelEl);
    }
    this._setDockVisible(false);
  }

  /**
   * Everything that must be true whenever this screen is up.
   *
   * There are two entry points — you died, or the match ended around you — and
   * they used to carry copies of this sequence. #45 added the body class to one
   * of them, #47 then "fixed" the other by duplicating the line rather than
   * removing the duplication, and a third entry point would have got it wrong
   * the same way. `hide()` was already the single close; this is the open.
   */
  _open() {
    this.active = true;
    this.root.classList.add('active');
    this._setDockVisible(true);
    // Hides the live controls: an eliminated player has nothing to cast, and a
    // player still alive at match end has nothing left to cast it at.
    document.body.classList.add('deathcam-active');
  }

  /** The two lanes this screen owns in #bottom-dock. */
  _setDockVisible(visible) {
    this.panelEl.hidden = !visible;
    this.hintEl.hidden = !visible;
  }

  /**
   * @param {object} info { killerId, killerName, placement, kills, damageDealt }
   */
  show(info) {
    this._open();
    this._since = performance.now();

    // The gym revives and re-kills; without this a death after a shown result
    // keeps the previous round's "WINNER" headline.
    this.headlineEl.textContent = 'ELIMINATED';
    this.headlineEl.classList.remove('win');

    this.placeEl.textContent = info.placement ? `#${info.placement}` : '—';
    this.killsEl.textContent = String(info.kills ?? 0);
    this.damageEl.textContent = String(Math.round(info.damageDealt ?? 0));

    if (info.killerId) {
      this._phase = 'killer';
      // The name is white, not the killer's roster colour — beside a red word
      // a roster colour would read as direction.
      this.subEl.innerHTML = `by <span class="who">${escapeHtml(info.killerName ?? 'an opponent')}</span>`;
      this.hintEl.textContent = 'watching your killer';
      cameraState.free = false;
      cameraState.followId = info.killerId;
    } else {
      // Nobody to watch. Skipping straight to free camera beats four seconds of
      // staring at your own corpse.
      this.subEl.textContent = 'by the zone';
      this._toFreeCamera();
    }
  }

  _toFreeCamera() {
    this._phase = 'free';
    this.hintEl.textContent = 'WASD / drag to fly · watching the match';
    this.controller.detachToFreeCamera();
  }

  update(now) {
    if (!this.active) return;
    if (this._phase === 'killer' && now - this._since >= KILLER_VIEW_MS) {
      this._toFreeCamera();
    }
  }

  /** The pot the survivors are still playing for. Wei in, display out. */
  setPot(potWei) {
    this.potEl.textContent = formatEth(potWei);
  }

  /** Match over while dead: swap the banner for the result. */
  showResult({ winnerName, isWinner }) {
    this._open();
    this.headlineEl.textContent = isWinner ? 'WINNER' : 'MATCH OVER';
    // Gold for money arriving; the default red for money that left.
    this.headlineEl.classList.toggle('win', isWinner);
    this.subEl.innerHTML = winnerName
      ? `<span class="who">${escapeHtml(winnerName)}</span> takes the pot`
      : '';
    this.hintEl.textContent = '';
    this._phase = 'result';
  }

  hide() {
    this._setDockVisible(false);
    document.body.classList.remove('deathcam-active');
    this.active = false;
    this._phase = 'idle';
    this.root.classList.remove('active');
    this.controller.attachToPlayer();
  }
}
